<template>
  <div
    data-cy="chat-window-container"
    :class="[
      'flex flex-col bg-white text-headline',
      isMobile ? 'fixed inset-0 z-50' : 'h-full rounded-lg shadow-lg',
    ]"
  >
    <div data-cy="chat-header" class="flex items-center justify-between border-b p-4">
      <div class="flex items-center space-x-2">
        <icon-chat class="text-primary-heavy-contrast" />
        <h3 class="font-medium">{{ t("chat.chatWithUs") }}</h3>
      </div>
      <button
        data-cy="chat-close-button"
        class="text-gray-500 hover:text-gray-700"
        @click="$emit('close')"
      >
        <icon-x />
      </button>
    </div>
    <div ref="messagesContainerRef" data-cy="messages-container" class="flex-1 overflow-y-auto p-4">
      <div
        v-if="agentIsTyping && !messages.length"
        data-cy="loading-indicator"
        class="flex h-full items-center justify-center"
      >
        <span class="animate-pulse">{{ t("common.loading") }}</span>
      </div>

      <template v-else>
        <div v-for="(message, index) in messages" :key="index">
          <chat-message :message="message" />
        </div>

        <chat-typing-indicator v-if="agentIsTyping" />
      </template>
    </div>
    <div data-cy="chat-input-container" class="border-t p-4">
      <chat-input
        ref="chatInputRef"
        :disabled="agentIsTyping"
        :loading="isUploading"
        :file-errors="fileErrors"
        @send="handleSendMessage"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useResponsive } from "@/composables/common";
import { useChatState } from "@/composables/state/chatSessionState";
import IconChat from "@/components/icons/IconChat.vue";
import IconX from "@/components/icons/IconX.vue";
import ChatMessage from "@/components/chat/shared/ChatMessage.vue";
import ChatInput from "@/components/chat/shared/ChatInput.vue";
import ChatTypingIndicator from "@/components/chat/shared/ChatTypingIndicator.vue";

defineEmits<{ close: [] }>();

const { t } = useI18n();
const { isMobile } = useResponsive();
const { messages, agentIsTyping, isUploading, fileErrors, sendMessage } = useChatState();

const messagesContainerRef = ref<HTMLElement | null>(null);
const chatInputRef = ref<InstanceType<typeof ChatInput> | null>(null);

const scrollToBottom = () => {
  if (!messagesContainerRef.value) {
    return;
  }

  messagesContainerRef.value.scrollTop = messagesContainerRef.value.scrollHeight;
};

const handleSendMessage = async (message: string, files: File[]) => {
  const result = await sendMessage(message, files);

  if (result.success) {
    chatInputRef.value?.clearInput();
  }
};

// Auto-scroll to bottom when messages change
watch(
  () => [messages.value.length, agentIsTyping.value],
  async () => {
    await nextTick();
    scrollToBottom();
  },
  { immediate: true }
);
</script>
